import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import config from '../Config';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders nothing and reports an empty token when TURNSTILE_SITE_KEY is unset, so
 * local and QA builds work without keys — the APIs skip verification in exactly the
 * same case.
 *
 * Tokens are single use and expire after 5 minutes, so callers must ask for a fresh
 * challenge after any failed submission via the exposed reset().
 *
 * The script is loaded once per page and shared by every instance.
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const el = existing || document.createElement('script');
    if (!existing) {
      el.id = SCRIPT_ID;
      el.src = SCRIPT_SRC;
      el.async = true;
      el.defer = true;
    }
    el.addEventListener('load', () => resolve(window.turnstile));
    el.addEventListener('error', () => reject(new Error('Turnstile script failed to load')));
    if (!existing) document.head.appendChild(el);
  });
  return scriptPromise;
}

export function turnstileEnabled() {
  return Boolean(config.TURNSTILE_SITE_KEY);
}

/**
 * @param {(token: string) => void} onToken called with the solution, or '' when it
 *   expires or errors so the caller can block submission again.
 */
const Turnstile = forwardRef(function Turnstile({ onToken, action }, ref) {
  const holder = useRef(null);
  const widgetId = useRef(null);

  const reset = () => {
    onToken('');
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetId.current);
      } catch { /* widget already gone */ }
    }
  };

  useImperativeHandle(ref, () => ({ reset }));

  useEffect(() => {
    if (!turnstileEnabled() || !holder.current) return undefined;

    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !turnstile || !holder.current) return;
        widgetId.current = turnstile.render(holder.current, {
          sitekey: config.TURNSTILE_SITE_KEY,
          action,
          // Pinned because the default 'auto' follows the visitor's own dark mode,
          // which drops a dark widget onto our light-only forms.
          theme: 'light',
          // Fills the container instead of a fixed 300px, so the widget lines up with
          // the full-width inputs it sits among.
          size: 'flexible',
          callback: (token) => onToken(token),
          // A token dies after 5 minutes. Someone writing a long support message
          // would be left holding a dead one, so re-challenge rather than dead-end.
          'expired-callback': reset,
          'error-callback': () => onToken(''),
        });
      })
      .catch(() => {
        if (!cancelled) onToken('');
      });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch { /* widget already gone */ }
        widgetId.current = null;
      }
    };
    // onToken and reset are stable for our callers; re-rendering the widget would
    // throw away a solved challenge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  if (!turnstileEnabled()) return null;

  return <div className="turnstile-holder" ref={holder} />;
});

export default Turnstile;
